import React, { useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    CircularProgress,
    Alert,
    Skeleton
} from '@mui/material';
import {
    LocalShipping as ShippingIcon,
    Receipt as ReceiptIcon,
    History as HistoryIcon,
    CheckCircle as CheckIcon,
    PendingActions as PendingIcon,
    Warning as WarningIcon,
    CurrencyRupee as RupeeIcon,
} from '@mui/icons-material';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { customerDashboardService } from '../../services/customerDashboardService';
import { useApiHandler } from '../../hooks/useApiHandler';
import { useNavigate } from 'react-router-dom';

const CustomerDashboard = () => {
    const { handleApiCallWithLoading, showError, showSuccess } = useApiHandler();
    const navigate = useNavigate();
    
    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        active_shipments: [],
        statistics: {},
        recent_activity: []
    });

    // Load dashboard data on component mount
    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await customerDashboardService.getDashboardStats();
            if (response.success) {
                setDashboardData(response.data);
            } else {
                setError(response.message || 'Failed to load dashboard data');
            }
        } catch (err) {
            console.error('Dashboard data loading error:', err);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        loadDashboardData();
    };

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Skeleton variant="rectangular" height={60} />
                </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2 }}>
                    <Skeleton variant="text" width="30%" height={40} />
                    <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
                </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                    <Skeleton variant="text" width="40%" height={40} />
                    <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
                </Paper>
            </Grid>
        </Grid>
    );

    if (loading) {
        return (
            <DashboardLayout title="Customer Dashboard" userType="customer">
                <LoadingSkeleton />
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout title="Customer Dashboard" userType="customer">
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={handleRefresh}>
                    Retry
                </Button>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Customer Dashboard" userType="customer">
            <Grid container spacing={3}>
                {/* Quick Actions */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<ShippingIcon />}
                                size="large"
                                onClick={() => navigate('/customer/track')}
                            >
                                Track Shipment
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ReceiptIcon />}
                                size="large"
                                onClick={() => navigate('/customer/invoices')}
                            >
                                View Invoices
                            </Button>
                            <Button
                                variant="text"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Refresh'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Recent Activity */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                Recent Activity
                            </Typography>
                            <Button
                                variant="text"
                                endIcon={<HistoryIcon />}
                                onClick={() => navigate('/customer/shipments')}
                            >
                                View All
                            </Button>
                        </Box>
                        {dashboardData.recent_activity?.length > 0 ? (
                            <List>
                                {dashboardData.recent_activity.map((activity, index) => (
                                    <React.Fragment key={activity.id}>
                                        <ListItem>
                                            <ListItemIcon>
                                                <ShippingIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box>
                                                        <Typography variant="body1">
                                                            {String(activity.activity || 'Unknown Activity')}
                                                        </Typography>
                                                        {activity.awb_no && activity.awb_no !== 'N/A' && (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'primary.main',
                                                                    cursor: 'pointer',
                                                                    textDecoration: 'underline',
                                                                    '&:hover': {
                                                                        color: 'primary.dark',
                                                                    }
                                                                }}
                                                                onClick={() => {
                                                                    // Navigate to tracking page with AWB number pre-filled
                                                                    navigate('/customer/track', { state: { awbNumber: activity.awb_no } });
                                                                }}
                                                            >
                                                                AWB: {activity.awb_no}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                secondary={`${String(activity.location || 'Unknown Location')} - ${String(activity.remark || 'No remarks')}`}
                                            />
                                            <Typography variant="caption" color="textSecondary">
                                                {String(activity.time_ago || 'Unknown time')}
                                            </Typography>
                                        </ListItem>
                                        {index < dashboardData.recent_activity.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    No Recent Activity
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Your recent shipment activities will appear here.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Shipment Summary */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Shipment Summary
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <ShippingIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Total Shipments"
                                    secondary={`${dashboardData.statistics?.total_shipments_this_month || 0} shipments (current month)`}
                                />
                            </ListItem>
                            <Divider />
                            <ListItem>
                                <ListItemIcon>
                                    <PendingIcon color="warning" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Pending Shipments"
                                    secondary={`${dashboardData.statistics?.pending_shipments || 0} shipments`}
                                />
                            </ListItem>
                            <Divider />
                            <ListItem>
                                <ListItemIcon>
                                    <CheckIcon color="success" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Delivered Shipments"
                                    secondary={`${dashboardData.statistics?.delivered_shipments || 0} shipments`}
                                />
                            </ListItem>
                            <Divider />
                            <ListItem
                                onClick={() => navigate('/customer/invoices', { state: { filterStatus: 'pending' } })}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: 'action.hover'
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <ReceiptIcon color="info" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Pending Invoices"
                                    secondary={`${dashboardData.statistics?.total_pending_invoices || 0} invoices`}
                                />
                            </ListItem>
                            <Divider />
                            <ListItem>
                                <ListItemIcon>
                                    <RupeeIcon color="info" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Pending Amount"
                                    secondary={`₹${Number(dashboardData.statistics?.pending_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                />
                            </ListItem>
                            <Divider />
                            <ListItem
                                onClick={() => navigate('/customer/invoices', { state: { filterStatus: 'overdue' } })}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: 'action.hover'
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <WarningIcon color="error" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Overdue Invoices"
                                    secondary={`${dashboardData.statistics?.total_overdue_invoices || 0} invoices`}
                                />
                            </ListItem>
                            <Divider />
                            <ListItem>
                                <ListItemIcon>
                                    <RupeeIcon color="error" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Overdue Amount"
                                    secondary={`₹${Number(dashboardData.statistics?.overdue_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                />
                            </ListItem>
                        </List>
                    </Paper>
                </Grid>

            </Grid>
        </DashboardLayout>
    );
};

export default CustomerDashboard; 